import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { BackButton, PickOne } from "../components/Choices";
import { Loading, Message, Page } from "../components/Page";
import { SaveStatus } from "../components/SaveStatus";
import { TriadPicker, isTriadComplete, type TriadCopy, type TriadDraft } from "../components/TriadPicker";
import { ApiError, api } from "../lib/api";
import { RATER_FINISH_COPY, RATER_INTRO_AFTER, RATER_OFFER_COPY } from "../lib/copy";
import type { DomainCode, RaterAnswerBody, RaterSession, RaterSessionMirror, RaterSessionO1 } from "../lib/types";
import { useAutosave } from "../lib/useAutosave";

// A rater link binds to the first browser that answers (httpOnly gp_rater cookie).
export const IN_USE_ELSEWHERE = "Someone has already started this link on another device. If that was you, finish it there.";
export const DEVICE_REQUIRED = "Please open this link in a browser that allows cookies.";
type RaterBlock = "in_use_elsewhere" | "device_required";

function raterBlock(e: unknown): RaterBlock | null {
  if (e instanceof ApiError && e.code === "in_use_elsewhere") return "in_use_elsewhere";
  if (e instanceof ApiError && e.code === "device_required") return "device_required";
  return null;
}

/** Rater pages: /r/:code. Public, by link code. Addendum B (o1) for v1 takes, the v3.1 test (o2) for v4 takes. */
export function RaterFlow() {
  const { code = "" } = useParams();
  const [session, setSession] = useState<RaterSession | null>(null);
  const [error, setError] = useState<"not_found" | "failed" | null>(null);
  const [blocked, setBlocked] = useState<RaterBlock | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getRaterSession(code)
      .then((s) => live && setSession(s))
      .catch((e) => live && setError(e instanceof ApiError && (e.status === 404 || e.status === 422) ? "not_found" : "failed"));
    return () => {
      live = false;
    };
  }, [code]);

  if (error === "not_found") {
    return (
      <Message title="This link isn't valid">
        <p>Check you've copied the whole link, or ask the person who sent it for a new one.</p>
      </Message>
    );
  }
  if (error === "failed") {
    return (
      <Message title="Something went wrong">
        <p>We couldn't load this page. Check your connection and reload.</p>
      </Message>
    );
  }
  if (!session) return <Loading />;
  if (blocked === "device_required") {
    return (
      <Message title="Cookies are needed">
        <p>{DEVICE_REQUIRED}</p>
      </Message>
    );
  }
  if (session.status !== "completed" && (session.inUseElsewhere || blocked === "in_use_elsewhere")) {
    return (
      <Message title="This link is in use">
        <p>{IN_USE_ELSEWHERE}</p>
      </Message>
    );
  }
  if (session.status === "completed") return <RaterFinish code={code} session={session} />;
  if (session.format === "mirror") return <MirrorQuestions code={code} session={session} onFinished={setSession} onBlocked={setBlocked} />;
  return <RaterQuestions code={code} session={session} onFinished={setSession} onBlocked={setBlocked} />;
}

type Behavior = { most?: DomainCode; least?: DomainCode };

function RaterQuestions({
  code,
  session,
  onFinished,
  onBlocked,
}: {
  code: string;
  session: RaterSessionO1;
  onFinished: (s: RaterSession) => void;
  onBlocked: (b: RaterBlock) => void;
}) {
  const items = session.items;
  const wordsStep = items.length;
  const familiarityStep = items.length + 1;
  const total = items.length + 2;

  const [behavior, setBehavior] = useState<Record<string, Behavior>>(session.answers.behavior);
  const [words, setWords] = useState<string[]>(session.answers.words ?? []);
  const [wordsTouched, setWordsTouched] = useState(session.answers.words !== null);
  const [familiarity, setFamiliarity] = useState<number | null>(session.answers.familiarity);
  // -1 = intro screen (only before the first answer).
  const [step, setStep] = useState(() => {
    if (session.answeredCount === 0) return -1;
    const i = items.findIndex((it) => !session.answers.behavior[it.id]);
    if (i >= 0) return i;
    if (session.answers.words === null) return wordsStep;
    return familiarityStep;
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const reload = useCallback(() => {
    api.getRaterSession(code).then(onFinished).catch(() => {});
  }, [code, onFinished]);
  const send = useCallback(
    async (itemCode: string, body: RaterAnswerBody) => {
      try {
        return await api.saveRaterAnswer(code, itemCode, body);
      } catch (e) {
        const b = raterBlock(e);
        if (b) onBlocked(b);
        throw e;
      }
    },
    [code, onBlocked],
  );
  const autosave = useAutosave<RaterAnswerBody>(send, reload, "already_submitted");

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo(0, 0);
    headingRef.current?.focus();
  }, [step]);

  const go = (to: number) => {
    setNotice(null);
    setStep(Math.max(0, Math.min(familiarityStep, to)));
  };

  const submit = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      onFinished(await api.submitRater(code));
    } catch (e) {
      setSubmitting(false);
      const b = raterBlock(e);
      if (b) {
        onBlocked(b);
        return;
      }
      if (e instanceof ApiError && e.code === "incomplete") {
        const missing = (e.details as { missing?: string[] } | undefined)?.missing ?? [];
        const at = items.findIndex((it) => missing.includes(it.id));
        if (at >= 0) {
          setStep(at);
          setNotice("This question still needs both picks.");
          return;
        }
      }
      setNotice(
        e instanceof ApiError && e.status === 0
          ? "We couldn't reach the server. Check your connection and try again."
          : "We couldn't send your answers. Try again.",
      );
    }
  };

  const blocked = autosave.pending || Object.keys(autosave.failed).length > 0;
  const name = session.name;

  if (step === -1) {
    return (
      <Page>
        <div className="mt-10 flex flex-1 flex-col">
          <h1 className="heading text-[30px] leading-[1.1]">Describe {name}</h1>
          {session.intro.split(/\n\s*\n/).map((para, i) => (
            <p key={i} className={`${i === 0 ? "mt-4" : "mt-3"} text-[17px] leading-relaxed`}>
              {para}
            </p>
          ))}
          {session.ownTest ? <p className="mt-3 text-[15px] leading-relaxed text-muted">{RATER_INTRO_AFTER}</p> : null}
          <div className="mt-auto pt-10">
            <button type="button" className="btn btn-primary w-full" onClick={() => go(0)}>
              Start
            </button>
          </div>
        </div>
      </Page>
    );
  }

  const counter = `${step + 1} / ${total}`;
  const heading = (text: string) => (
    <h1 ref={headingRef} tabIndex={-1} className="heading mt-2 text-[25px] leading-[1.18] outline-none sm:text-[28px]">
      <span className="sr-only">Question {step + 1}. </span>
      {text}
    </h1>
  );
  const numeral = (
    <span aria-hidden="true" className="numeral block text-[34px] text-accent-text">
      {String(step + 1).padStart(2, "0")}
    </span>
  );
  const noticeEl = notice ? (
    <p role="alert" className="mb-4 rounded-[16px] border border-line bg-panel px-3 py-2 text-[15px]">
      {notice}
    </p>
  ) : null;

  if (step < items.length) {
    const item = items[step]!;
    const pick = behavior[item.id] ?? {};
    const setPick = (next: Behavior) => {
      setBehavior((b) => ({ ...b, [item.id]: next }));
      if (next.most && next.least) autosave.save(item.id, { most: next.most, least: next.least });
    };
    return (
      <Page right={counter} footer={false}>
        <div className="mt-6 flex flex-1 flex-col" key={item.id}>
          {noticeEl}
          {numeral}
          {heading(item.prompt)}
          <MostLeast name={name} options={item.options} value={pick} onChange={setPick} />
          <div className="mt-auto pt-6">
            <div className="flex gap-3">
              <BackButton onClick={() => go(step === 0 ? -1 : step - 1)} />
              <button
                type="button"
                className="btn btn-primary flex-1"
                disabled={!pick.most || !pick.least}
                onClick={() => go(step + 1)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
        <SaveStatus autosave={autosave} />
      </Page>
    );
  }

  if (step === wordsStep) {
    const w = session.words;
    const toggle = (key: string) => {
      const next = words.includes(key) ? words.filter((k) => k !== key) : words.length < w.maxWords ? [...words, key] : words;
      if (next === words) return;
      setWords(next);
      setWordsTouched(true);
      autosave.save(w.id, { words: next });
    };
    const next = () => {
      if (!wordsTouched) {
        setWordsTouched(true);
        autosave.save(w.id, { words });
      }
      go(familiarityStep);
    };
    return (
      <Page right={counter} footer={false}>
        <div className="mt-6 flex flex-1 flex-col">
          {noticeEl}
          {numeral}
          {heading(w.prompt)}
          <p className="mt-3 text-[15px] font-semibold" aria-live="polite">
            {words.length} of {w.maxWords}
          </p>
          <div role="group" aria-label="Words" className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {w.options.map((o) => {
              const selected = words.includes(o.key);
              const full = !selected && words.length >= w.maxWords;
              return (
                <button
                  key={o.key}
                  type="button"
                  aria-pressed={selected}
                  disabled={full}
                  onClick={() => toggle(o.key)}
                  className={`min-h-12 rounded-full border px-4 text-[15px] font-medium ${
                    selected ? "border-chosen bg-chosen-tint text-ink ring-1 ring-chosen" : "border-line bg-panel text-ink"
                  } disabled:opacity-40`}
                >
                  {o.word}
                </button>
              );
            })}
          </div>
          {/* Sticky so the count and Next stay in reach while scrolling the word list. */}
          <div className="sticky bottom-0 mt-auto bg-ground pt-4 pb-1">
            <p className="mb-2 text-[13px] text-muted" aria-hidden="true">
              {words.length} of {w.maxWords} chosen
            </p>
            <div className="flex gap-3">
              <BackButton onClick={() => go(step - 1)} />
              <button type="button" className="btn btn-primary flex-1" disabled={words.length < w.minWords} onClick={next}>
                Continue
              </button>
            </div>
            <SaveStatus autosave={autosave} />
          </div>
        </div>
      </Page>
    );
  }

  const f = session.familiarity;
  return (
    <Page right={counter} footer={false}>
      <div className="mt-6 flex flex-1 flex-col">
        {noticeEl}
        {numeral}
        {heading(f.prompt)}
        <div role="group" aria-label="Your answer" className="mt-[clamp(2rem,6vh,4rem)] grid grid-cols-2 gap-3">
          {f.options.map((o) => {
            const selected = familiarity === o.value;
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  setFamiliarity(o.value);
                  autosave.save(f.id, { value: o.value });
                }}
                className={`serif min-h-[88px] rounded-[16px] border text-[21px] ${
                  selected ? "border-chosen bg-chosen-tint text-ink ring-1 ring-chosen" : "border-line bg-panel text-ink"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <div className="mt-auto pt-6">
          <div className="flex gap-3">
            <BackButton onClick={() => go(step - 1)} />
            <button
              type="button"
              className="btn btn-primary flex-1"
              disabled={familiarity === null || blocked || submitting}
              onClick={submit}
            >
              {submitting ? "Sending…" : "Send"}
            </button>
          </div>
          <SaveStatus autosave={autosave} />
        </div>
      </div>
    </Page>
  );
}

/** The gift sets' words, about the person being described. */
const setCopy = (name: string): TriadCopy => ({
  statusMost: `Most like ${name}: tap one.`,
  statusLeast: "Least like them: tap one of the other two.",
  tagMost: `Most like ${name}`,
  tagLeast: "Least like them",
});

// Question screens follow the take's layout (Edward, Oct 22: "D"): the answers sit a steady
// 32-64px under the question and only Back and Continue are pinned to the bottom.

/**
 * The v3.1 test (o2), 16 screens: six everyday scenes (pick the one most like them), nine sets of
 * three statements (most like them, then least like them, as the self-test's gift sets) and how
 * well they know the person. Answers autosave; a gift set saves once both picks are made.
 */
function MirrorQuestions({
  code,
  session,
  onFinished,
  onBlocked,
}: {
  code: string;
  session: RaterSessionMirror;
  onFinished: (s: RaterSession) => void;
  onBlocked: (b: RaterBlock) => void;
}) {
  const { scenarios, sets, name } = session;
  const setsFrom = scenarios.length;
  const familiarityStep = scenarios.length + sets.length;
  const total = familiarityStep + 1;

  const [scenePicks, setScenePicks] = useState<Record<string, DomainCode>>(session.answers.scenarios);
  const [drafts, setDrafts] = useState<Record<string, TriadDraft>>(session.answers.sets);
  const [familiarity, setFamiliarity] = useState<number | null>(session.answers.familiarity);
  // -1 = intro screen (only before the first answer); otherwise resume at the first unanswered.
  const [step, setStep] = useState(() => {
    if (session.answeredCount === 0) return -1;
    const s = scenarios.findIndex((x) => !session.answers.scenarios[x.id]);
    if (s >= 0) return s;
    const t = sets.findIndex((x) => !session.answers.sets[x.id]);
    if (t >= 0) return setsFrom + t;
    return familiarityStep;
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const reload = useCallback(() => {
    api.getRaterSession(code).then(onFinished).catch(() => {});
  }, [code, onFinished]);
  const send = useCallback(
    async (itemCode: string, body: RaterAnswerBody) => {
      try {
        return await api.saveRaterAnswer(code, itemCode, body);
      } catch (e) {
        const b = raterBlock(e);
        if (b) onBlocked(b);
        throw e;
      }
    },
    [code, onBlocked],
  );
  const autosave = useAutosave<RaterAnswerBody>(send, reload, "already_submitted");

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo(0, 0);
    headingRef.current?.focus();
  }, [step]);

  const go = (to: number) => {
    setNotice(null);
    setStep(Math.max(0, Math.min(familiarityStep, to)));
  };

  const submit = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      onFinished(await api.submitRater(code));
    } catch (e) {
      setSubmitting(false);
      const b = raterBlock(e);
      if (b) {
        onBlocked(b);
        return;
      }
      if (e instanceof ApiError && e.code === "incomplete") {
        const missing = (e.details as { missing?: string[] } | undefined)?.missing ?? [];
        const s = scenarios.findIndex((x) => missing.includes(x.id));
        const t = sets.findIndex((x) => missing.includes(x.id));
        if (s >= 0 || t >= 0) {
          setStep(s >= 0 ? s : setsFrom + t);
          setNotice(s >= 0 ? "This question still needs an answer." : "This question still needs both picks.");
          return;
        }
      }
      setNotice(
        e instanceof ApiError && e.status === 0
          ? "We couldn't reach the server. Check your connection and try again."
          : "We couldn't send your answers. Try again.",
      );
    }
  };

  const blocked = autosave.pending || Object.keys(autosave.failed).length > 0;

  if (step === -1) {
    return (
      <Page>
        <div className="mt-10 flex flex-1 flex-col">
          <h1 className="heading text-[30px] leading-[1.1]">Describe {name}</h1>
          {session.intro.split(/\n\s*\n/).map((para, i) => (
            <p key={i} className={`${i === 0 ? "mt-4" : "mt-3"} text-[17px] leading-relaxed`}>
              {para}
            </p>
          ))}
          {session.ownTest ? <p className="mt-3 text-[15px] leading-relaxed text-muted">{RATER_INTRO_AFTER}</p> : null}
          <div className="mt-auto pt-10">
            <button type="button" className="btn btn-primary w-full" onClick={() => go(0)}>
              Start
            </button>
          </div>
        </div>
      </Page>
    );
  }

  const counter = `${step + 1} / ${total}`;
  const heading = (text: string) => (
    <h1 ref={headingRef} tabIndex={-1} className="heading mt-2 text-[25px] leading-[1.18] outline-none sm:text-[28px]">
      <span className="sr-only">Question {step + 1}. </span>
      {text}
    </h1>
  );
  const numeral = (
    <span aria-hidden="true" className="numeral block text-[34px] text-accent-text">
      {String(step + 1).padStart(2, "0")}
    </span>
  );
  const noticeEl = notice ? (
    <p role="alert" className="mb-4 rounded-[16px] border border-line bg-panel px-3 py-2 text-[15px]">
      {notice}
    </p>
  ) : null;
  const nav = (canContinue: boolean) => (
    <div className="mt-auto pt-6">
      <div className="flex gap-3">
        <BackButton onClick={() => go(step === 0 ? -1 : step - 1)} />
        <button type="button" className="btn btn-primary flex-1" disabled={!canContinue} onClick={() => go(step + 1)}>
          Continue
        </button>
      </div>
    </div>
  );

  if (step < setsFrom) {
    const scene = scenarios[step]!;
    const value = scenePicks[scene.id];
    return (
      <Page right={counter} footer={false}>
        <div className="mt-6 flex flex-1 flex-col" key={scene.id}>
          {noticeEl}
          {numeral}
          {heading(scene.prompt)}
          <p className="mt-3 text-[14px] text-muted">{session.instructions?.partA ?? `Pick the one most like ${name}.`}</p>
          <div className="mt-[clamp(2rem,6vh,4rem)]">
            <PickOne
            options={scene.options}
            value={value}
            onPick={(v) => {
              const d = v as DomainCode;
              setScenePicks((p) => ({ ...p, [scene.id]: d }));
              autosave.save(scene.id, { most: d });
            }}
            />
          </div>
          {nav(!!value)}
        </div>
        <SaveStatus autosave={autosave} />
      </Page>
    );
  }

  if (step < familiarityStep) {
    const set = sets[step - setsFrom]!;
    const draft = drafts[set.id] ?? {};
    return (
      <Page right={counter} footer={false}>
        <div className="mt-6 flex flex-1 flex-col" key={set.id}>
          {noticeEl}
          {numeral}
          {heading(session.instructions?.partB ?? `Pick the one most like ${name}, then the one least like them.`)}
          <div className="mt-[clamp(2rem,6vh,4rem)]">
            <TriadPicker
              options={set.options}
              value={draft}
              copy={setCopy(name)}
              onChange={(d) => {
                setDrafts((all) => ({ ...all, [set.id]: d }));
                if (isTriadComplete(d)) autosave.save(set.id, { most: d.most, least: d.least });
              }}
            />
          </div>
          {nav(isTriadComplete(draft))}
        </div>
        <SaveStatus autosave={autosave} />
      </Page>
    );
  }

  const f = session.familiarity;
  return (
    <Page right={counter} footer={false}>
      <div className="mt-6 flex flex-1 flex-col">
        {noticeEl}
        {numeral}
        {heading(f.prompt)}
        <div role="group" aria-label="Your answer" className="mt-[clamp(2rem,6vh,4rem)] grid grid-cols-2 gap-3">
          {f.options.map((o) => {
            const selected = familiarity === o.value;
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={selected}
                onClick={() => {
                  setFamiliarity(o.value);
                  autosave.save(f.id, { value: o.value });
                }}
                className={`serif min-h-[88px] rounded-[16px] border text-[21px] ${
                  selected ? "border-chosen bg-chosen-tint text-ink ring-1 ring-chosen" : "border-line bg-panel text-ink"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <div className="mt-auto pt-6">
          <div className="flex gap-3">
            <BackButton onClick={() => go(step - 1)} />
            <button type="button" className="btn btn-primary flex-1" disabled={familiarity === null || blocked || submitting} onClick={submit}>
              {submitting ? "Sending…" : "Send"}
            </button>
          </div>
          <SaveStatus autosave={autosave} />
        </div>
      </div>
    </Page>
  );
}

/**
 * Four cards; the rater picks "Most like {Name}" then "Least like {Name}". Both picks are
 * marked on the cards, and the same card can't be both.
 */
export function MostLeast({
  name,
  options,
  value,
  onChange,
}: {
  name: string;
  options: { value: DomainCode; text: string }[];
  value: Behavior;
  onChange: (v: Behavior) => void;
}) {
  const [mode, setMode] = useState<"most" | "least">(value.most && !value.least ? "least" : "most");
  const [hint, setHint] = useState<string | null>(null);

  const tap = (d: DomainCode) => {
    setHint(null);
    if (mode === "most") {
      const next: Behavior = { most: d, least: value.least === d ? undefined : value.least };
      onChange(next);
      if (!next.least) setMode("least");
    } else {
      if (value.most === d) {
        setHint(`Pick a different one for least like ${name}.`);
        return;
      }
      onChange({ ...value, least: d });
      if (!value.most) setMode("most");
    }
  };

  return (
    <div className="mt-[clamp(2rem,6vh,4rem)]">
      <div role="group" aria-label="What you're picking" className="grid grid-cols-2 gap-2">
        {(["most", "least"] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className={`min-h-11 rounded-full border px-2 text-[14px] font-semibold ${
              mode === m ? "border-ink bg-ink text-ground" : "border-line bg-panel text-ink"
            }`}
          >
            {m === "most" ? `Most like ${name}` : `Least like ${name}`}
          </button>
        ))}
      </div>
      <p className="mt-3 min-h-6 text-[14px] text-muted" aria-live="polite">
        {hint ??
          (mode === "most" ? `Tap the one that's most like ${name}.` : `Now tap the one that's least like ${name}.`)}
      </p>
      <div role="group" aria-label="Descriptions" className="mt-2 grid grid-cols-2 gap-3">
        {options.map((o) => {
          const tag = value.most === o.value ? "most" : value.least === o.value ? "least" : null;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={tag !== null}
              aria-label={`${o.text}${tag === "most" ? ", most like" : tag === "least" ? ", least like" : ""}`}
              onClick={() => tap(o.value)}
              className={`relative flex min-h-[128px] items-end rounded-[16px] border p-3 pt-9 text-left text-[15px] leading-snug font-semibold ${
                tag === "most"
                  ? "border-chosen bg-chosen-tint text-ink ring-1 ring-chosen"
                  : tag === "least"
                    ? "border-ink bg-soft text-ink"
                    : "border-line bg-panel text-ink"
              }`}
            >
              {tag ? (
                <span
                  aria-hidden="true"
                  className={`eyebrow absolute top-3 left-3 rounded-[8px] px-1.5 py-0.5 ${
                    tag === "most" ? "bg-chosen text-white" : "border border-ink text-ink"
                  }`}
                >
                  {tag === "most" ? "Most" : "Least"}
                </span>
              ) : null}
              {o.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RaterFinish({ code, session }: { code: string; session: RaterSession }) {
  const invite = session.finish?.invite;
  const c = RATER_FINISH_COPY;
  const name = session.name;
  useEffect(() => {
    if (invite) void api.raterOfferEvent(code, "seen");
  }, [code, invite]);
  return (
    <Page>
      <div className="mt-10 flex flex-1 flex-col">
        <p className="eyebrow text-accent-text">{c.eyebrow}</p>
        <h1 className="heading mt-2 text-balance text-[30px] leading-[1.1]">{invite ? c.heading(name) : "Thank you, your answers are in!"}</h1>
        {session.finish?.text ? (
          <p className="mt-4 text-pretty text-[16px] leading-relaxed text-muted">{invite ? session.finish.text.replace(/^Thank you\.\s*/, "") : session.finish.text}</p>
        ) : null}
        {invite ? <OwnTestOffer code={code} name={name} invite={invite} /> : null}
      </div>
    </Page>
  );
}

/** "Not now? We'll email you the link": once per rater link; the address is kept only with the tick. */
function EmailMeLater({ code }: { code: string }) {
  const f = RATER_FINISH_COPY;
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setState("sending");
    try {
      await api.emailRaterOffer(code, email, consent);
      setState("sent");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) return setState("sent");
      setState("idle");
      setError(
        err instanceof ApiError && err.status === 422
          ? "Hmm, that email address doesn't look quite right. Mind checking it?"
          : err instanceof ApiError && err.status === 404
            ? "This only works in the browser you answered in, but you can use the button above instead."
            : err instanceof ApiError && err.status === 429
              ? "We've sent that address all the emails we can for today, but you can use the button above instead."
              : "Something went wrong sending that. Mind trying again?",
      );
    }
  };
  if (state === "sent") {
    return (
      <p role="status" className="rounded-[16px] border border-line bg-panel p-5 text-[16px]" data-testid="email-me-sent">
        {f.laterSent}
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="rounded-[16px] border border-line bg-panel p-5 sm:p-6" aria-labelledby="email-me-title" data-testid="email-me">
      <h3 id="email-me-title" className="heading text-[20px] leading-tight">
        {f.laterTitle}
      </h3>
      <p className="mt-2 text-pretty text-[16px] leading-relaxed">{f.laterText}</p>
      <label htmlFor="email-me-input" className="mt-4 block text-[14px] font-semibold">
        {f.laterLabel}
      </label>
      <input
        id="email-me-input"
        type="email"
        autoComplete="email"
        inputMode="email"
        required
        maxLength={254}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="field-input mt-2 w-full"
      />
      <label className="mt-3 flex items-start gap-3 text-[15px] leading-snug">
        <input type="checkbox" className="mt-0.5 h-6 w-6 shrink-0 accent-accent-text" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{f.laterConsent}</span>
      </label>
      {error ? (
        <p role="alert" className="mt-3 text-[15px]">
          {error}
        </p>
      ) : null}
      <button type="submit" className="btn btn-secondary mt-4 w-full" disabled={state === "sending"}>
        {state === "sending" ? "Sending…" : f.laterButton}
      </button>
      <p className="mt-3 text-[13px] text-muted">{f.laterNote}</p>
    </form>
  );
}

const money = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);

/**
 * The button says the price (Edward, Nov 3: "Take my Gifts Profile for $29", not a "$29, paid
 * once" line): their price, "for free", or just the words with no paywall.
 */
const ctaLabel = (words: string, offer: NonNullable<NonNullable<RaterSession["finish"]>["invite"]>["offer"]) =>
  !offer ? words : offer.priceCents === 0 ? `${words} for free` : `${words} for ${money(offer.priceCents)}`;

/** A special price shown struck through ("$29 $19 for you", "Free for you"), and an event's code. The usual price is on the button. */
function OfferPrice({ offer }: { offer: NonNullable<NonNullable<RaterSession["finish"]>["invite"]>["offer"] }) {
  if (!offer || (!offer.special && !offer.code)) return null;
  return (
    <div className="mt-5" data-testid="offer-price">
      {offer.special ? (
        <p className="text-[17px] leading-snug">
          <span className="mr-2 text-muted line-through" aria-label={`Usually ${money(offer.fullPriceCents)}`}>
            {money(offer.fullPriceCents)}
          </span>
          <strong className="text-[22px]">{offer.priceCents === 0 ? "Free for you" : `${money(offer.priceCents)} for you`}</strong>
        </p>
      ) : null}
      {offer.special && offer.priceCents > 0 ? <p className="mt-1 text-[14px] text-muted">That's our thank-you for answering. It's already applied at checkout, so there's nothing to type.</p> : null}
      {offer.code ? (
        <p className="mt-2 text-[15px]">
          Want it for less? Use the code <strong className="font-mono tracking-[0.04em]">{offer.code}</strong> at checkout.
        </p>
      ) : null}
    </div>
  );
}

/** The Freedom × Clarity map, as on the home page: four places, Free at the top, Clear on the right. */
function MiniMap() {
  const q = RATER_OFFER_COPY.quadrants;
  return (
    <figure className="mt-5" aria-label="The Freedom and Clarity map">
      <p className="eyebrow text-center text-muted">Free</p>
      <div className="mt-2 flex items-stretch gap-2">
        <p className="eyebrow text-muted [writing-mode:vertical-rl] rotate-180 text-center">Unclear</p>
        <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
          {q.map((x) => (
            <div key={x.name} className={`rounded-[12px] border p-3 ${x.name === "Activated" ? "border-accent-text bg-soft" : "border-line bg-panel"}`}>
              <p className="font-bold">{x.name}</p>
              <p className="mt-1 text-pretty text-[13px] leading-snug text-muted">{x.line}</p>
            </div>
          ))}
        </div>
        <p className="eyebrow text-muted [writing-mode:vertical-rl] text-center">Clear</p>
      </div>
      <p className="eyebrow mt-2 text-center text-muted">Bound</p>
    </figure>
  );
}

/**
 * Their own test, as a mini sales page (Edward, Oct 26; reworked Oct 31 and Nov 3): what the
 * Gifts Profile is before any price ("they can't even visualize anything they're going to get"):
 * the idea and the map, the three layers, what their answers give the person; then their own, with
 * the price on the button, and "email me the link" for later.
 */
function OwnTestOffer({ code, name, invite }: { code: string; name: string; invite: NonNullable<NonNullable<RaterSession["finish"]>["invite"]> }) {
  const c = RATER_OFFER_COPY;
  const f = RATER_FINISH_COPY;
  const clicked = () => void api.raterOfferEvent(code, "clicked");
  const turn = useRef<HTMLHeadingElement>(null);
  const jump = () => {
    const smooth = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    turn.current?.scrollIntoView?.({ behavior: smooth ? "smooth" : "auto", block: "start" });
    turn.current?.focus({ preventScroll: true });
  };
  return (
    <section aria-labelledby="own-test" className="mt-10 flex flex-col gap-10">
      <div>
        <h2 className="heading text-balance text-[24px] leading-[1.15]">{c.ideaTitle}</h2>
        <p className="mt-3 text-pretty text-[16px] leading-relaxed">{c.ideaText}</p>
        <button type="button" className="btn btn-primary mt-5 w-full" onClick={jump}>
          {c.jump}
        </button>
        <MiniMap />
        <p className="mt-3 text-pretty text-[15px] leading-relaxed text-muted">{c.mapNote}</p>
      </div>

      <div>
        <h3 className="heading text-balance text-[22px] leading-tight">{c.layersTitle}</h3>
        <ol className="mt-4 flex flex-col gap-4">
          {c.layers.map((l, i) => (
            <li key={l.title} className="flex gap-3">
              <span className="numeral mt-0.5 w-6 shrink-0 text-[20px] text-accent-text" aria-hidden="true">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="font-bold">{l.title}</p>
                <p className="mt-1 text-pretty text-[15px] leading-relaxed text-muted">{l.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div data-testid="helped">
        <h3 className="heading text-balance text-[22px] leading-tight">{f.helpedTitle(name)}</h3>
        <p className="mt-3 text-pretty text-[15px] leading-relaxed text-muted">{f.helpedText(name)}</p>
        <ul className="mt-3 flex flex-col gap-3">
          {f.shows.map((x) => (
            <li key={x.title} className="flex gap-3 text-[16px] leading-snug">
              <span aria-hidden="true" className="mt-[7px] size-2 shrink-0 rounded-full bg-accent-text" />
              <span className="min-w-0">
                <strong>{x.title}</strong>: {x.text}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-[16px] border border-accent-text bg-panel p-5 sm:p-6" data-testid="own-test-card">
        <p className="eyebrow text-accent-text">{c.eyebrow}</p>
        <h2 id="own-test" ref={turn} tabIndex={-1} className="heading mt-2 scroll-mt-16 text-balance text-[26px] leading-[1.15] outline-none">
          {f.turnHeading}
        </h2>
        <p className="mt-3 text-pretty text-[16px] leading-relaxed">{f.turnText}</p>
        <OfferPrice offer={invite.offer} />
        <a href={invite.url} className="btn btn-primary mt-5 w-full no-underline" onClick={clicked}>
          {ctaLabel(c.cta, invite.offer)}
        </a>
        <p className="mt-3 text-center text-[13px] text-muted">{c.meta}</p>
        <p className="mt-4 text-pretty text-[15px] leading-relaxed text-muted">{f.backTo(name)}</p>
      </div>

      <EmailMeLater code={code} />
    </section>
  );
}
